import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getAppraiserUsedTmvPath } from 'client/data/utils/used-tmv-experiment';
import { head, tail } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleModel, VehicleEntities } from 'client/data/models/vehicle-v2';
import { AppraisalModel, buildAllStylesAppraisalFilteredPath, AppraisalEntities } from 'client/data/models/appraisal';
import { TRADE_IN_VALUE } from 'site-modules/shared/constants/appraisal/appraisal';
import { AppraiseNowForm } from 'site-modules/shared/components/appraise-now-form/appraise-now-form';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { getAppraisalValues } from 'site-modules/shared/utils/appraisal/common-utils';

import { EstimatedValuesHeader } from './estimated-values-header';
import { EstimatedValuesData } from './estimated-values-data';

import './estimated-values.scss';

/**
 * Renders component with estimated values for vehicle.
 */
export function EstimatedValuesUI({
  vehicle = {},
  appraisalStyles = [],
  isMobile = false,
  showHeaderCaption = true,
  defaultMobileValue = TRADE_IN_VALUE,
  styleName = '',
  className = '',
  onContinue = null,
  containerClassName = 'rounded p-1 p-lg-1_5 pb-1_5 mt-md-1_5',
  usedTmv = null,
}) {
  const { makeName = '', modelName = '', year = '' } = vehicle || {};
  const vehicleName = `${year} ${makeName} ${modelName} ${styleName}`.trim();
  const dataBeforeCollapse = head(appraisalStyles);
  const dataInsideCollapse = tail(appraisalStyles);
  const { minAppraisalValue, maxAppraisalValue } = getAppraisalValues({ appraisalStyles });
  const appraisalWidgetInfo = dataBeforeCollapse?.tmv?.conditions || usedTmv;

  return (
    <div className={classnames('estimated-values', className)} data-tracking-parent="edm-entry-estimated-values">
      <div className={classnames('estimated-values-container', containerClassName)}>
        <EstimatedValuesHeader
          isMobile={isMobile}
          vehicleName={vehicleName}
          showHeaderCaption={showHeaderCaption}
          minAppraisalValue={minAppraisalValue}
          maxAppraisalValue={maxAppraisalValue}
        />
        <MinHeightWrapper componentType="ESTIMATED_VALUES">
          {dataBeforeCollapse ? (
            <EstimatedValuesData
              styleName={dataBeforeCollapse.styleName}
              appraisalWidgetInfo={appraisalWidgetInfo}
              defaultMobileValue={defaultMobileValue}
              isMobile={isMobile}
            />
          ) : (
            <div className="mt-1">
              <i>Estimated values are currently unavailable.</i>
            </div>
          )}
          {Boolean(dataInsideCollapse.length) && (
            <ContentCollapse
              textClosed="See more"
              textOpen="See less"
              justify="justify-content-start"
              btnContainerClasses="m-0 p-0"
              btnClasses="p-0 size-16 text-primary-darker more-info-btn"
              btnTextClasses="fw-normal"
              showBtnBelow
            >
              {dataInsideCollapse.map(style => (
                <EstimatedValuesData
                  key={style.styleId}
                  styleName={style.styleName}
                  appraisalWidgetInfo={style.tmv.conditions}
                  defaultMobileValue={defaultMobileValue}
                  isMobile={isMobile}
                />
              ))}
            </ContentCollapse>
          )}
        </MinHeightWrapper>
        {onContinue && (
          <MinHeightWrapper
            className="estimated-values-vehicle-dropdowns-wrapper mt-1_5"
            componentType="ESTIMATED_VALUE_VEHICLE_DROPDOWNS"
          >
            <RenderWhenViewable>
              <div className="estimated-values-vehicle-dropdowns">
                <div className="text-gray-darker mb-1_5">
                  <div className="d-inline-block pos-r ps-1_75 me-0_5">
                    <i className="car-icon icon-edmunds-car text-primary size-24 me-0_5" aria-hidden />
                    <span className="fw-bold">Did you know?</span>
                  </div>
                  <span>
                    Vehicle appraisals vary significantly by zip code, mileage and condition. Get your most accurate
                    price in just minutes!
                  </span>
                </div>
                <AppraiseNowForm isMobile={isMobile} vehicle={vehicle} onContinue={onContinue} />
              </div>
            </RenderWhenViewable>
          </MinHeightWrapper>
        )}
      </div>
    </div>
  );
}

EstimatedValuesUI.propTypes = {
  vehicle: VehicleEntities.StyleWithoutSubmodels,
  usedTmv: PropTypes.shape({}),
  appraisalStyles: AppraisalEntities.Styles,
  isMobile: PropTypes.bool,
  showHeaderCaption: PropTypes.bool,
  defaultMobileValue: PropTypes.string,
  styleName: PropTypes.string,
  className: PropTypes.string,
  onContinue: PropTypes.func,
  containerClassName: PropTypes.string,
};

export const stateToPropsConfig = {
  appraisalStyles: bindToPath(
    ({ vehicle: { makeSlug, modelSlug, year } }) =>
      buildAllStylesAppraisalFilteredPath({ make: makeSlug, model: modelSlug, year }),
    AppraisalModel
  ),
  usedTmv: bindToPath(
    ({ vehicle, isExtendedReport, useGraphqlUsedTmv }) =>
      getAppraiserUsedTmvPath({ styleId: vehicle?.id, isExtendedReport, useGraphqlUsedTmv }),
    VehicleModel
  ),
};

export const EstimatedValues = connectToModel(EstimatedValuesUI, stateToPropsConfig);
